"use client"

import React from 'react';
import Img from 'next/image';
import Link from 'next/link';

import { SpaceCountryFlag } from 'app/(authenticated)/(member)/_components/CountryCodeFlag';
import { Card } from 'app/(authenticated)/(member)/_components/Card';
import { CommunitiesQuery } from 'generated/operations';
import { CommunityCardFooter } from './CommunityCardFooter';
import { MEMBER_ROUTES } from 'utils/routes';
import { useRouter } from 'next/navigation';
import { useCommunity } from 'contexts/communityApp';

type CommunitiesNode = Extract<
  CommunitiesQuery['communities']['objs'][number],
  {}
>

interface CommunityCardProps {
  community: CommunitiesNode;
}

export const CommunityCard: React.FC<CommunityCardProps> = ({ community }) => {
  const router = useRouter();
  const { chooseCommunity } = useCommunity();

  const showMembers = (community.membersCount ?? 0) >= 10;
  const showConnections = (community?.connectionsCount ?? 0) >= 10;

  return (
    <Card className='!p-0 !gap-0'>
      <Link
        href={MEMBER_ROUTES.SPACE.getPath(community.subdomain || community.domain || '')}
        onClick={(e) => {
          e.preventDefault();
          chooseCommunity(community);
          router.push(MEMBER_ROUTES.SPACE.getPath(community.subdomain || community.domain || ''));
        }}>

        <div className='relative w-full h-[170px] rounded-t-2xl overflow-hidden'>
          <Img
            src={community.image}
            alt={`${community.name} cover img`}
            className='object-cover w-full h-full'
            fill
          />

          {(community.location || community.locationGlobal) && (
            <div className='absolute top-[12px] left-[12px] group'>
              <div
                className='bg-gray-100 rounded-full flex justify-start items-center gap-1 p-[3px] overflow-hidden transition-[width] duration-1000 min-w-[24px] w-6 group-hover:w-auto group-hover:pr-[8px]'>
                <div className='flex-shrink-0'>
                  <SpaceCountryFlag community={community} />
                </div>
                <div
                  className='text-gray-700 text-xs font-medium whitespace-nowrap opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs transition-all duration-1000'>
                  {community.locationGlobal ? (
                    'Global'
                  ) : (community.location?.partialName)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className='px-4 pt-4 flex flex-col gap-2 pb-2'>
          <div className='flex items-center gap-2'>
            <div className='relative w-[32px] h-[32px] rounded-full overflow-hidden'>
              <Img
                src={community.logo || '/logo/default-community-logo.svg'}
                alt={`${community.name} logo`}
                fill
                className='object-cover'
              />
            </div>
            <h3 className='text-small font-semibold line-clamp-1'>{community.name}</h3>
          </div>

          <div className='flex items-center gap-1 text-xs'>
            {showMembers && (
              <>
                <span className='font-semibold text-gray-950'>{community.membersCount}</span>
                <span className='font-medium text-gray-600'>
                {community.membersCount === 1 ? 'Member' : 'Members'}
              </span>
              </>
            )}

            {showMembers && showConnections && (
              <span className='mx-1 text-gray-600'>•</span>
            )}

            {showConnections && (
              <>
                <span className='font-semibold text-gray-950'>{community.connectionsCount}</span>
                <span className='font-medium text-gray-600'>
                {community.connectionsCount === 1 ? 'Connection' : 'Connections'}
              </span>
              </>
            )}
          </div>

          {/* Description */}
          {community.description && (
            <p className='text-gray-700 line-clamp-3 text-small font-regular'>{community.description}</p>
          )}
        </div>
      </Link>
      <CommunityCardFooter community={community} />
    </Card>
  );
}; 
